if (!customElements.get("variant-swatch-buttons")) {
  customElements.define(
    "variant-swatch-buttons",
    class variantSwatchButtons extends HTMLElement {
      constructor() {
        super();
        this.onColorSwatches();
      }

      onColorSwatches() {
        const variantList = this.getVariantData() || [];
        const swatchButtonList = this.querySelectorAll("color-swatch-variant");
        const result = Object.values(
          variantList.reduce((total, item) => {
            let k = `${item[this.dataset.colorSwatch]}`;
            if (!total[k]) {
              total[k] = { ...item, count: 1 };
            } else {
              total[k].count += 1;
            }
            return total;
          }, {})
        );

        if (swatchButtonList.length > 0) {
          swatchButtonList.forEach((button) => {
            result.forEach((data) => {
              if (data[this.dataset.colorSwatch] === button.dataset.value) {
                if (
                  this.dataset.colorSwatchStyle === "image" &&
                  data.featured_image != null
                ) {
                  const attributes = {
                    style: `--swatch-background-image: url(${
                      data.featured_image.src
                    }); --swatch-background-color: ${
                      data[this.dataset.colorSwatch]
                    };`,
                    "data-variant-id": `${data.id}`,
                  };
                  for (var key in attributes) {
                    button.setAttribute(key, attributes[key]);
                  }
                } else {
                  const attributes = {
                    style: `--swatch-background-color: ${
                      data[this.dataset.colorSwatch]
                    };`,
                    "data-variant-id": `${data.id}`,
                  };
                  for (var key in attributes) {
                    button.setAttribute(key, attributes[key]);
                  }
                }
              }
            });
          });
        }
      }

      getVariantData() {
        const script = this.querySelector('[type="application/json"]');
        if (!script || !script.textContent.trim()) return [];
        this.variantData =
          this.variantData ||
          JSON.parse(script.textContent);
        return this.variantData;
      }
    }
  );
}

// Each card owns its scroll controls, including cards inserted by section rendering.
if (!customElements.get("card-swatch-strip")) {
  customElements.define("card-swatch-strip", class CardSwatchStrip extends HTMLElement {
    connectedCallback() {
      this.setupFrame = requestAnimationFrame(() => this.setup());
    }

    setup() {
      this.viewport = this.querySelector("variant-swatch-buttons");
      this.previous = this.querySelector("[data-swatch-previous]");
      this.next = this.querySelector("[data-swatch-next]");
      if (!this.viewport || !this.previous || !this.next) return;
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.previous.addEventListener("click", (event) => this.move(event, -1), options);
      this.next.addEventListener("click", (event) => this.move(event, 1), options);
      this.viewport.addEventListener("scroll", () => this.schedule(), { ...options, passive: true });
      this.viewport.addEventListener("focusin", (event) => {
        const chip = event.target.closest("color-swatch-variant");
        if (chip) this.reveal(chip);
      }, options);
      this.viewport.addEventListener("keydown", (event) => this.onKeydown(event), options);
      window.addEventListener("resize", () => this.schedule(), options);
      this.resizeObserver = new ResizeObserver(() => this.schedule());
      this.resizeObserver.observe(this);
      this.resizeObserver.observe(this.viewport);
      this.mutationObserver = new MutationObserver(() => {
        this.viewport.variantData = null;
        this.viewport.onColorSwatches?.();
        this.schedule();
      });
      this.mutationObserver.observe(this.viewport, { childList: true, subtree: true, characterData: true });
      this.viewport.onColorSwatches?.();
      this.schedule();
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.setupFrame);
      cancelAnimationFrame(this.layoutFrame);
      this.events?.abort();
      this.resizeObserver?.disconnect();
      this.mutationObserver?.disconnect();
    }

    schedule() {
      cancelAnimationFrame(this.layoutFrame);
      this.layoutFrame = requestAnimationFrame(() => this.update());
    }

    update() {
      const chips = this.viewport.querySelectorAll("color-swatch-variant");
      // Compare with the full strip, so resizing can remove controls again.
      const overflow = this.clientWidth > 0 && this.viewport.scrollWidth > this.clientWidth + 1;
      this.toggleAttribute("data-overflow", overflow);
      this.previous.hidden = this.next.hidden = !overflow;
      if (!overflow || !chips.length) {
        this.previous.disabled = this.next.disabled = true;
        return;
      }
      const box = this.viewport.getBoundingClientRect();
      const style = getComputedStyle(this.viewport);
      const left = box.left + parseFloat(style.paddingLeft);
      const right = box.right - parseFloat(style.paddingRight);
      const first = chips[0].getBoundingClientRect();
      const last = chips[chips.length - 1].getBoundingClientRect();
      const rtl = style.direction === "rtl";
      this.previous.disabled = rtl ? first.right <= right + 1 : first.left >= left - 1;
      this.next.disabled = rtl ? last.left >= left - 1 : last.right <= right + 1;
    }

    move(event, direction) {
      event.preventDefault();
      event.stopPropagation();
      const rtl = getComputedStyle(this.viewport).direction === "rtl";
      this.viewport.scrollBy({
        left: direction * (rtl ? -1 : 1) * Math.max(27, this.viewport.clientWidth * 0.8),
        behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth"
      });
    }

    reveal(chip) {
      const box = this.viewport.getBoundingClientRect();
      const item = chip.getBoundingClientRect();
      const style = getComputedStyle(this.viewport);
      const left = box.left + parseFloat(style.paddingLeft);
      const right = box.right - parseFloat(style.paddingRight);
      const delta = item.left < left ? item.left - left : item.right > right ? item.right - right : 0;
      if (delta) this.viewport.scrollBy({ left: delta, behavior: "auto" });
    }

    onKeydown(event) {
      const chip = event.target.closest("color-swatch-variant");
      if (!chip || !["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
      event.preventDefault();
      event.stopPropagation();
      const chips = Array.from(this.viewport.querySelectorAll("color-swatch-variant"));
      const index = chips.indexOf(chip);
      const rtl = getComputedStyle(this.viewport).direction === "rtl";
      const step = (event.key === "ArrowRight" ? 1 : -1) * (rtl ? -1 : 1);
      const destination = event.key === "Home" ? 0 : event.key === "End" ? chips.length - 1 : Math.max(0, Math.min(chips.length - 1, index + step));
      chips[destination].focus({ preventScroll: true });
      this.reveal(chips[destination]);
    }
  });
}

if (!customElements.get("color-swatch-variant")) {
  customElements.define(
    "color-swatch-variant",
    class ColorSwatchVariant extends HTMLElement {
      constructor() {
        super();
        this.variantId = this.dataset.variantId;
        this.productHandle = this.dataset.productHandle;
        this.productCard = this.closest(".product__card");

        this.addEventListener("click", this.onClickHandler.bind(this));
        this.addEventListener("keydown", (event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            event.stopPropagation();
            this.click();
          }
        });
      }

      onClickHandler(event) {
        event.preventDefault();
        event.stopPropagation();
        this.variantId = this.dataset.variantId;
        if (!this.variantId || !this.productCard) return;
        const selectedVariant = this.variantId;
        this.productCard.dataset.selectedCardVariant = selectedVariant;
        let url = `/products/${this.productHandle}?variant=${this.variantId}&view=colorswatch`;
        fetch(url)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            if (this.productCard.dataset.selectedCardVariant !== selectedVariant) return;
            this.preserveCollectionLinks(html);
            this.updateThumbnail(html);
            this.updateTitle(html);
            this.updatePrice(html);
          })
          .catch((e) => {
            console.error(e);
          });
        // Color swatch button active/remove classList
        const colorButtonList = this.productCard.querySelectorAll(
          ".product--color-swatch"
        );
        colorButtonList.forEach((button) => {
          button.classList.remove("checked-color");
          if (button.hasAttribute("aria-pressed")) {
            button.setAttribute("aria-pressed", "false");
          }
        });
        this.classList.add("checked-color");
        if (this.hasAttribute("aria-pressed")) {
          this.setAttribute("aria-pressed", "true");
        }
      }

      // Keep the collection URL already present on this card when its color response uses canonical URLs.
      preserveCollectionLinks(html) {
        const currentLink = this.productCard.querySelector('.product__card--link, .product__card-title--link');
        if (!currentLink) return;
        const current = new URL(currentLink.getAttribute('href'), location.origin);
        const prefix = current.pathname.split('/products/')[0];
        if (!/\/collections\/[^/]+$/.test(prefix)) return;
        html.querySelectorAll('.product__card--link, .product-grid-item__title a').forEach(link => {
          const target = new URL(link.getAttribute('href'), location.origin);
          const handle = target.pathname.split('/products/')[1];
          if (target.origin !== current.origin || handle !== this.productHandle) return;
          target.pathname = prefix + '/products/' + handle;
          link.setAttribute('href', target.pathname + target.search + target.hash);
        });
      }

      updateThumbnail(html) {
        const pilot = this.dataset.tapstitchProduct === "true";
        const destination = pilot
          ? this.productCard.querySelector(".media img")
          : this.productCard.querySelector(".media").firstChild;
        const source = pilot
          ? html.querySelector(".media img")
          : html.querySelector(".media").firstChild;
        const gallery = this.productCard.querySelector("card-product-gallery");
        const gallerySelected = gallery?.showVariant(this.variantId, source);
        if (!gallerySelected && source && destination) {
          destination.src = source.src;
          destination.srcset = source.srcset;
          if (pilot) destination.alt = source.alt;
          gallery?.resetToPrimaryImage();
        }
        // Thumbnail link update
        const linkDestination = this.productCard.querySelector(
          ".product__card--link"
        );
        const linkSource = html
          .querySelector(".product__card--link")
          .getAttribute("href");

        if (linkDestination && linkSource)
          linkDestination.setAttribute("href", `${linkSource}`);
      }

      updateTitle(html) {
        if (this.dataset.tapstitchProduct === "true") {
          const sourceLink = html.querySelector(".product-grid-item__title a");
          const titleLink = this.productCard.querySelector(".product__card-title--link");
          if (sourceLink && titleLink) titleLink.setAttribute("href", sourceLink.getAttribute("href"));
        }
        const destination = this.productCard.querySelector(
          ".product-grid-item__title"
        );
        const source = html.querySelector(".product-grid-item__title");

        if (source && destination) destination.innerHTML = source.innerHTML;
      }

      updatePrice(html) {
        const destination = this.productCard.querySelector(".price");
        const source = html.querySelector(".price");
        if (source && destination) destination.innerHTML = source.innerHTML;
      }
    }
  );
}

/* Card gallery and full-title scrolling; scoped to product-card.liquid. */
if (!customElements.get("card-product-gallery")) {
  customElements.define("card-product-gallery", class CardProductGallery extends HTMLElement {
    connectedCallback() {
      this.setupFrame = requestAnimationFrame(() => this.setup());
    }
    setup() {
      this.track = this.querySelector("[data-gallery-track]");
      this.link = this.querySelector(".product__card--link");
      this.slides = Array.from(this.querySelectorAll("[data-gallery-slide]"));
      if (!this.track || !this.link || this.slides.length < 2) return;
      this.events?.abort();
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.previous = this.querySelector("[data-gallery-previous]");
      this.next = this.querySelector("[data-gallery-next]");
      this.position = this.querySelector("[data-gallery-position]");
      this.status = this.querySelector("[data-gallery-status]");
      this.index = 0;
      this.interacted = false;
      this.previous.hidden = this.next.hidden = false;
      this.previous.addEventListener("click", e => this.move(e, -1), options);
      this.next.addEventListener("click", e => this.move(e, 1), options);
      this.link.addEventListener("keydown", e => this.onKeydown(e), options);
      this.track.addEventListener("scroll", () => this.schedule(), { ...options, passive: true });
      this.track.addEventListener("wheel", () => this.prepareInteraction(), { ...options, passive: true });
      this.track.addEventListener("pointerdown", e => {
        if (!e.isPrimary || e.button !== 0) return;
        this.gesture = { id: e.pointerId, x: e.clientX, y: e.clientY, dragged: false };
        this.suppressUntil = 0;
        this.prepareInteraction();
      }, { ...options, passive: true });
      this.track.addEventListener("pointermove", e => {
        if (!this.gesture || this.gesture.id !== e.pointerId) return;
        const dx = Math.abs(e.clientX - this.gesture.x);
        const dy = Math.abs(e.clientY - this.gesture.y);
        if (dx > 12 && dx > dy * 1.25) {
          this.gesture.dragged = true;
          this.suppressUntil = performance.now() + 500;
        }
      }, { ...options, passive: true });
      const finish = e => {
        if (this.gesture?.id !== e.pointerId) return;
        if (this.gesture.dragged) this.suppressUntil = performance.now() + 500;
        this.gesture = null;
      };
      this.track.addEventListener("pointerup", finish, options);
      this.track.addEventListener("pointercancel", finish, options);
      this.link.addEventListener("click", e => {
        if (e.detail > 0 && performance.now() < (this.suppressUntil || 0)) {
          e.preventDefault();
          e.stopPropagation();
        }
      }, { ...options, capture: true });
      this.resizeObserver = new ResizeObserver(() => this.show(this.index, false, false));
      this.resizeObserver.observe(this.track);
      this.hydrate(0);
      this.update(false);
      this.classList.add("is-ready");
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.setupFrame);
      cancelAnimationFrame(this.scrollFrame);
      this.events?.abort();
      this.resizeObserver?.disconnect();
    }
    hydrate(index) {
      const img = this.slides[index]?.querySelector("img");
      if (!img || img.hasAttribute("src")) return;
      if (img.dataset.srcset) img.srcset = img.dataset.srcset;
      img.src = img.dataset.src;
      delete img.dataset.srcset;
      delete img.dataset.src;
    }
    prepareInteraction() {
      this.interacted = true;
      [this.index - 1, this.index, this.index + 1].forEach(index => this.hydrate(index));
    }
    schedule() {
      cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = requestAnimationFrame(() => {
        const box = this.track.getBoundingClientRect();
        if (!box.width) return;
        const center = (box.left + box.right) / 2;
        let distance = Infinity;
        this.slides.forEach((slide, index) => {
          const rect = slide.getBoundingClientRect();
          const nextDistance = Math.abs((rect.left + rect.right) / 2 - center);
          if (nextDistance < distance) { distance = nextDistance; this.index = index; }
        });
        if (this.interacted) this.prepareInteraction();
        this.update(this.interacted);
      });
    }
    show(index, smooth = true, announce = true) {
      if (!this.track || !this.slides?.length) return;
      this.index = Math.max(0, Math.min(this.slides.length - 1, index));
      this.hydrate(this.index);
      if (announce) this.prepareInteraction();
      const viewport = this.track.getBoundingClientRect();
      if (!viewport.width) return;
      const slide = this.slides[this.index].getBoundingClientRect();
      const rtl = getComputedStyle(this.track).direction === "rtl";
      const left = rtl ? slide.right - viewport.right : slide.left - viewport.left;
      this.track.scrollTo({
        left: this.track.scrollLeft + left,
        behavior: smooth && !matchMedia("(prefers-reduced-motion: reduce)").matches ? "smooth" : "auto"
      });
      this.update(announce);
    }
    move(event, direction) {
      event.preventDefault();
      event.stopPropagation();
      this.show(this.index + direction);
    }
    onKeydown(event) {
      if (event.altKey || event.ctrlKey || event.metaKey ||
          !["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
      event.preventDefault();
      event.stopPropagation();
      const rtl = getComputedStyle(this.track).direction === "rtl";
      const direction = (event.key === "ArrowRight" ? 1 : -1) * (rtl ? -1 : 1);
      this.show(event.key === "Home" ? 0 : event.key === "End" ? this.slides.length - 1 : this.index + direction);
    }
    update(announce) {
      this.previous.disabled = this.index === 0;
      this.next.disabled = this.index === this.slides.length - 1;
      this.position.textContent = (this.index + 1) + " / " + this.slides.length;
      this.link.setAttribute("aria-label", this.dataset.productTitle + " — image " + (this.index + 1) + " of " + this.slides.length);
      this.slides.forEach((slide, index) => slide.setAttribute("aria-hidden", String(index !== this.index)));
      if (announce && this.announcedIndex !== this.index) {
        this.status.textContent = "Image " + (this.index + 1) + " of " + this.slides.length;
        this.announcedIndex = this.index;
      }
    }
    showVariant(variantId, source) {
      if (!this.slides) return false;
      const swatches = this.closest(".product__card")?.querySelector("variant-swatch-buttons");
      const variant = swatches?.getVariantData?.().find(item => String(item.id) === String(variantId));
      const imageIds = [variant?.featured_image?.id, variant?.featured_media?.preview_image?.id, variant?.featured_media?.id]
        .filter(Boolean).map(String);
      let index = this.slides.findIndex(slide => imageIds.includes(slide.dataset.imageId));
      // Shopify image IDs and media IDs can differ. Match the rendered variant
      // image URL as well, ignoring CDN resize parameters and host aliases.
      if (index < 0 && source) {
        const path = value => {
          try { return new URL(value, location.href).pathname; } catch { return null; }
        };
        const sourcePath = path(source.getAttribute("src"));
        if (sourcePath) index = this.slides.findIndex(slide => {
          const image = slide.querySelector("img");
          return image && path(image.getAttribute("src") || image.dataset.src) === sourcePath;
        });
      }
      if (index < 0) return false;
      this.show(index, false, true);
      return true;
    }
    resetToPrimaryImage() { this.show(0, false, true); }
  });
}

if (!customElements.get("card-title-scroll")) {
  customElements.define("card-title-scroll", class CardTitleScroll extends HTMLElement {
    connectedCallback() {
      this.setupFrame = requestAnimationFrame(() => this.setup());
    }
    setup() {
      this.events?.abort();
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.addEventListener("pointerdown", e => {
        if (!e.isPrimary || e.button !== 0 || this.scrollWidth <= this.clientWidth + 1) return;
        this.gesture = { id: e.pointerId, x: e.clientX, y: e.clientY, scroll: this.scrollLeft, mouse: e.pointerType === "mouse", dragged: false };
        this.suppressUntil = 0;
      }, { ...options, passive: true });
      this.addEventListener("pointermove", e => {
        const gesture = this.gesture;
        if (!gesture || gesture.id !== e.pointerId) return;
        const dx = e.clientX - gesture.x;
        const dy = e.clientY - gesture.y;
        if (Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy) * 1.25) {
          gesture.dragged = true;
          this.suppressUntil = performance.now() + 500;
          if (gesture.mouse) {
            e.preventDefault();
            if (!this.hasPointerCapture(e.pointerId)) this.setPointerCapture(e.pointerId);
            this.scrollLeft = gesture.scroll - dx;
          }
        }
      }, options);
      const finish = e => {
        if (this.gesture?.id !== e.pointerId) return;
        if (this.gesture.dragged) this.suppressUntil = performance.now() + 500;
        if (this.hasPointerCapture(e.pointerId)) this.releasePointerCapture(e.pointerId);
        this.gesture = null;
      };
      this.addEventListener("pointerup", finish, options);
      this.addEventListener("pointercancel", finish, options);
      this.addEventListener("click", e => {
        if (e.detail > 0 && performance.now() < (this.suppressUntil || 0)) {
          e.preventDefault();
          e.stopPropagation();
        }
      }, { ...options, capture: true });
      this.addEventListener("keydown", e => {
        if (e.altKey || e.ctrlKey || e.metaKey ||
            !["ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key) ||
            this.scrollWidth <= this.clientWidth + 1) return;
        e.preventDefault();
        e.stopPropagation();
        const behavior = matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth";
        const rtl = getComputedStyle(this).direction === "rtl";
        if (e.key === "Home" || e.key === "End") {
          this.scrollTo({ left: e.key === "Home" ? 0 : (rtl ? -1 : 1) * (this.scrollWidth - this.clientWidth), behavior });
        } else this.scrollBy({ left: e.key === "ArrowRight" ? 80 : -80, behavior });
      }, options);
      this.resizeObserver = new ResizeObserver(() => {
        this.toggleAttribute("data-overflow", this.scrollWidth > this.clientWidth + 1);
      });
      this.resizeObserver.observe(this);
      this.resizeObserver.observe(this.querySelector("a"));
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.setupFrame);
      this.events?.abort();
      this.resizeObserver?.disconnect();
    }
  });
}
